<script setup>
import { computed, ref, watch } from 'vue'
import { api } from '@/lib/http.js'
import { entityPath } from '@/lib/entities.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import { useI18n } from 'vue-i18n'
import { reportError } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import UiButton from '@/components/ui/UiButton.vue'
import RecordLink from './RecordLink.vue'
import RelatedActions from './RelatedActions.vue'

/**
 * Connections of an account or a contact (D-202), as Dynamics' Connections: any account or
 * person, with what they are to this one — "Ewa Kowalska — advisor" here is "Jan — client" on her
 * card. Made as a sentence: a person or a company, who, then what they are to this one — only
 * the roles that fit the pair (a colleague is a person's person, a supplier a company's company).
 */
const props = defineProps({ entity: { type: String, required: true }, record: { type: Object, required: true }, canEdit: Boolean })
const meta = useMeta()
const ui = useUi()
const { t } = useI18n()
const list = ref([])
const failed = ref(false)
const adding = ref(false)
const kind = ref(props.entity === 'account' ? 'account' : 'contact')
const target = ref(null)
const role = ref('')
const note = ref('')
const busy = ref(false)
const path = computed(() => `/${entityPath(props.entity)}/${props.record.uuid}/connections`)
// The roles for what the other is to this one ("contact>account": a company to this person).
const roles = computed(() => meta.dictionary('connection_role').entries.filter((e) => !Array.isArray(e.meta?.pairs) || !e.meta.pairs.length || e.meta.pairs.includes(`${props.entity}>${kind.value}`)))
const targetLabel = ref('')
watch(kind, () => {
  target.value = null
  role.value = ''
})
async function load() {
  failed.value = false
  try {
    list.value = (await api(path.value)).data
  } catch {
    failed.value = true
  }
}
watch(() => props.record.uuid, load, { immediate: true })
async function connect() {
  busy.value = true
  try {
    list.value = (await api(path.value, { method: 'POST', body: { entity: kind.value, uuid: target.value, role: role.value, note: note.value || null } })).data
    adding.value = false
    target.value = null
    role.value = ''
    note.value = ''
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}
async function remove(c) {
  if (!(await ui.ask({ title: t('related.detachTitle'), message: t('related.detachLinkMessage', { record: c.record.label, from: props.record.label }), confirmLabel: t('related.detach') }))) return
  try {
    await api(`${path.value}/${c.uuid}`, { method: 'DELETE' })
    await load()
  } catch (e) {
    reportError(e)
  }
}
</script>

<template>
  <section class="card" data-testid="connections">
    <header class="flex items-center gap-2 border-b border-default px-3 py-2">
      <i class="fa-solid fa-people-arrows text-sm text-faint" aria-hidden="true"></i>
      <h3 class="flex-1 text-sm font-semibold">{{ $t('relationships.connections') }} <span class="font-normal text-faint">({{ list.length }})</span></h3>
      <RelatedActions :can-link="canEdit && !failed" :linking="adding" testid="connections" @link="adding = !adding" />
    </header>
    <div v-if="adding" class="space-y-2 border-b border-default p-3">
      <div class="flex flex-wrap items-center gap-2">
        <div class="flex rounded-lg border border-default p-0.5 text-sm" role="radiogroup">
          <button v-for="k in ['contact', 'account']" :key="k" type="button" role="radio" :aria-checked="kind === k" :data-testid="`connection-kind-${k}`"
            :class="['rounded-md px-2.5 py-1', kind === k ? 'bg-brand-600 text-white' : 'text-muted hover:bg-[var(--surface-3)]']" @click="kind = k">
            <i :class="['fa-solid mr-1', k === 'account' ? 'fa-building' : 'fa-user']" aria-hidden="true"></i>{{ $t(k === 'account' ? 'relationships.aCompany' : 'relationships.aPerson') }}
          </button>
        </div>
        <RefPicker :key="kind" v-model="target" :entity="kind" :allow-create="false" class="min-w-48 flex-1" data-testid="connection-target" @select="(r) => (targetLabel = r?.label || '')" />
      </div>
      <!-- read as a sentence: who is what to this record -->
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span class="font-medium">{{ targetLabel || $t(kind === 'account' ? 'relationships.theCompany' : 'relationships.thePerson') }}</span>
        <span class="text-muted">{{ $t('relationships.isFor', { record: record.label }) }}</span>
        <select v-model="role" class="input w-auto min-w-48 flex-1" data-testid="connection-role">
          <option value="">{{ $t('relationships.roleOf') }}…</option>
          <option v-for="e in roles" :key="e.key" :value="e.key">{{ meta.localized(e.label) }}</option>
        </select>
      </div>
      <input v-model="note" class="input" maxlength="500" :placeholder="$t('relationships.notePlaceholder')" />
      <div class="flex justify-end gap-2">
        <UiButton size="sm" @click="adding = false">{{ $t('common.cancel') }}</UiButton>
        <UiButton size="sm" variant="primary" icon="fa-link" :disabled="!target || !role" :loading="busy" data-testid="connection-save" @click="connect">{{ $t('related.attach') }}</UiButton>
      </div>
    </div>
    <p v-if="failed" class="px-3 py-3 text-sm text-faint">{{ $t('relationships.needsNetwork') }}</p>
    <p v-else-if="!list.length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="c in list" :key="c.uuid" class="flex items-center gap-2 px-3 py-2" :data-testid="`connection-${c.record.uuid}`">
        <i :class="['fa-solid text-xs', c.record.entity === 'account' ? 'fa-building text-sky-600' : 'fa-user text-emerald-600']" aria-hidden="true"></i>
        <div class="min-w-0 flex-1">
          <RecordLink :entity="c.record.entity" :uuid="c.record.uuid" class="block truncate text-sm font-medium hover:underline">{{ c.record.label }}</RecordLink>
          <p class="truncate text-xs text-muted">{{ [meta.dictionaryLabel('connection_role', c.role), c.note].filter(Boolean).join(' · ') }}</p>
        </div>
        <UiButton v-if="canEdit" size="xs" variant="ghost" icon="fa-link-slash" class="shrink-0 opacity-60 hover:text-red-600 hover:opacity-100" :title="$t('related.detach')" :aria-label="$t('related.detach')" @click="remove(c)" />
      </li>
    </ul>
  </section>
</template>
